import { formatMinor } from '@/kova/format'
import type {
  Block, Cell, ChipCell, Column, KvItem, NoteBlock, SubCell, TableBlock,
} from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'
import { exactMinorBig, isCurrencyCode } from '@/lib/money/money'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Ledger › Journal explorer, read from `GET /v1/admin/ledger/postings`
 * (go-wallet#386, merged in go-wallet#393).
 *
 * # This is the evidence, and a read of it must not manufacture a fact
 *
 * The journal is what invariant 9 points at when it says a decision must be
 * reconstructable six months later, and it is the table an operator quotes in a
 * dispute with the institution. Every honesty rule the dashboard adapter
 * follows applies here with more force, because the reader is not scanning for
 * a red card — they are answering a question about one movement of money and
 * will act on whatever this screen says.
 *
 * Three of those rules shaped nearly every function below.
 *
 * **An empty page is a claim about the journal.** The backend goes to real
 * trouble to make sure it never accidentally makes one: an unparseable query is
 * a 400 rather than an unfiltered list, and a filter naming an account that does
 * not exist is a 404 rather than an empty result (go-wallet#394, #404). The
 * console's half of that bargain is to never let a page read as the whole
 * result. `has_more` is stated in the footer, in a note of its own, and in the
 * summary — three places, because "the first 200 of four million" and "the
 * journal" are the same picture with one sentence between them.
 *
 * **A missing provider reference is either evidence of a violation or nothing
 * at all, and only the legs can tell you which.** FR-5.5 requires a provider
 * reference on every posting that moved customer value; an internal posting
 * legitimately has none. `touches_customer_value` is what distinguishes them,
 * and the list read does not compute it — reading the legs for a list would be
 * a join across the two largest tables in the system. So on this screen the
 * pairing is reported as *undetermined*, never as *fine*. Rendering an absent
 * flag as `false` would paint every unreferenced posting as legitimately
 * internal, which is the one reading that makes an FR-5.5 breach invisible.
 *
 * **The journal is append-only, so a wrong posting is answered, not fixed.**
 * `corrects_posting_id` and `reverses_posting_id` are that answer, and they get
 * a column. The design corpus draws a "Post correction" composer beneath this
 * table; it is deliberately not rendered. This section ships with no mutating
 * control at all — not because the work is unfinished, but because every
 * finding here resolves through an attributed compensating posting written by
 * the backend's own seam or it does not resolve (console law 5, invariant 2).
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_ledger.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * `not_built` and `unavailable` are deliberately not one state: the first is a
 * roadmap fact and the second is an incident, and an operator does something
 * different about each.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

/**
 * Minor units and an explicit ISO code, always together (invariants 3 and 4).
 *
 * `amount_minor` is `number | string`, and the union is the point rather than
 * defensiveness. `JSON.parse` rounds any integer past 2^53 before a line of
 * console code runs, so go-wallet is moving this field to a decimal string
 * (go-wallet#411) — and it cannot move until this console reads both forms
 * (#38), because `Number.isSafeInteger("500")` is `false` and a console that
 * gated on it would report every €5.00 balance as unreadable.
 *
 * These types are mirrored here rather than imported from `./dashboard`, which
 * declares the same four shapes, for exactly that reason: its `MoneyView` still
 * narrows `amount_minor` to `number`, and importing half a vocabulary while
 * redeclaring the other half is worse than mirroring all of it in one place.
 */
export interface MoneyView {
  readonly amount_minor: number | string
  readonly currency: string
}

export interface FigureView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  /** Absent, never zero, when the figure is not available. */
  readonly value?: MoneyView
  readonly provenance?: ProvenanceView
}

/** One `ledger_legs` row. Signed: debits positive, credits negative. */
export interface LedgerLegView {
  readonly id: number
  readonly account_id: number
  readonly account_class: string
  readonly account_scope: string
  readonly amount: FigureView
}

/**
 * One journal entry, as `ledgerPostingView` serialises it.
 *
 * The optional fields are `omitempty` on the Go side and their absence carries
 * meaning in every case:
 *
 *   - `provider_ref` absent means the posting carries none — FR-5.5's evidence
 *     is missing, which is legitimate on an internal posting and is not on a
 *     posting that moved customer value.
 *   - `touches_customer_value` absent means THE READ DID NOT LOOK. It is a
 *     `*bool` precisely so that "no" and "we did not check" are different
 *     answers; the list read always omits it and the detail read always sends
 *     it.
 *   - `legs` absent means the same thing one level up: the list read does not
 *     join them, so this screen has no amounts at all.
 */
export interface LedgerPostingView {
  readonly id: number
  readonly kind: string
  readonly posting_type: string
  readonly currency: string
  readonly provider_ref?: string
  readonly idempotency_key?: string
  readonly corrects_posting_id?: number
  readonly reverses_posting_id?: number
  readonly occurred_at: string
  readonly posted_at: string
  readonly actor: string
  readonly reason_code?: string
  readonly touches_customer_value?: boolean
  readonly legs?: readonly unknown[]
}

/**
 * The cap, on the wire, on every list.
 *
 * `has_more` is answered by reading one row past the cap rather than by
 * counting the table, and `next_cursor` is the last id on the page — present
 * only when there is more, so a cursor is never offered into nothing.
 */
export interface PageView {
  readonly limit: number
  readonly returned: number
  readonly has_more: boolean
  readonly next_cursor?: number
}

export interface LedgerPostingsResponse {
  readonly generated_at: string
  readonly page: PageView
  /**
   * Typed `unknown[]` on purpose. The rows are the only part of this payload a
   * loop dereferences, and a loader that throws on a malformed row breaks the
   * contract that it resolves for every answer the backend gives. Narrowing
   * each row explicitly is what makes that impossible rather than unlikely.
   */
  readonly postings: readonly unknown[]
}

export const LEDGER_POSTINGS_PATH = '/ledger/postings'

/** The single-posting read. The backend answers with a bare `ledgerPostingView`. */
export function ledgerPostingPath(id: number): string {
  return `${LEDGER_POSTINGS_PATH}/${String(id)}`
}

const SOURCE = 'postgres · ledger_postings (GET /v1/admin/ledger/postings)'
const POSTING_SOURCE = 'postgres · ledger_postings + ledger_legs (GET /v1/admin/ledger/postings/{id})'

/**
 * Ours, and operationally authoritative — which is not the same as final.
 *
 * The list read carries no per-figure provenance, so this is asserted rather
 * than derived. `operational` is the honest word: the journal is our system of
 * record for reads and business logic, and FinXP's records still win every
 * discrepancy (invariant 2). The screen says so in words as well, because a
 * one-word authority chip is not where a dispute gets settled.
 */
const AUTHORITY = 'operational'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The same posture `availabilityOf` takes on the dashboard: an unrecognised
 * value from the backend resolves to the reading that shows the operator less
 * confidence, never more. A future fourth state defaulting to "available" would
 * put an unverified amount on a journal leg.
 */
function availabilityOf(raw: string): Availability {
  return AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

function reasonFor(availability: Availability, raw: string, reason: string | undefined): string {
  if (reason !== undefined && reason !== '') return reason
  if (availability === 'available') return ''
  if (!AVAILABILITIES.has(raw)) {
    return `The backend reported availability "${raw}", which this console does not know. Treated as unreadable.`
  }
  return 'Could not be read. This is not an amount of zero.'
}

/** A string we were actually sent, or the empty string. Never `undefined` downstream. */
function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * A posting id: the number, `'unreadable'` if one arrived we cannot state
 * exactly, or `null` if none arrived at all.
 *
 * The three are not interchangeable and collapsing them is how an operator ends
 * up at the wrong posting. `JSON.parse` hands back a double for a Go `int64`, so
 * an id past 2^53 has already lost its last digits — and a posting id that is
 * plausibly wrong is one somebody will look up, find, and reason about the
 * wrong movement of money from. `null` means the posting answers nothing, which
 * is the ordinary case and must not read as a failure.
 */
function idRef(value: unknown): number | 'unreadable' | null {
  if (value === undefined || value === null) return null
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : 'unreadable'
}

const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER)
const MIN_SAFE = BigInt(Number.MIN_SAFE_INTEGER)

/**
 * Minor units we can state EXACTLY, as a bigint, or nothing.
 *
 * The number branch checks `Number.isSafeInteger` first because by the time a
 * JSON number reaches here the damage is already done — `JSON.parse` rounded it
 * — and rendering the rounded value gives an amount that is wrong in its last
 * places and looks entirely plausible.
 *
 * The string branch NEVER routes through `Number()`. That is the whole reason
 * go-wallet is moving to a string encoding: `Number("9007199254740993")` is
 * `...992`, so a `Number.isSafeInteger` check applied after the conversion
 * PASSES for the one value that must fail, and the console prints a wrong
 * amount while believing it verified one. `BigInt` on a digits-only string is
 * exact at any magnitude, so the check is the regex and nothing else.
 */
/**
 * Delegates to `money.exactMinorBig` rather than reimplementing it.
 *
 * #40 produced four private readers of this one field and they had drifted
 * before the branch was a day old — this copy accepted a leading `+` and
 * trimmed whitespace, neither of which `strconv.FormatInt` emits. The shared
 * pair is one implementation per rule: `exactMinor` for `number` consumers,
 * bounded at 2^53 because they cannot hold more, and `exactMinorBig` here,
 * unbounded on the string branch because a bigint can state it exactly.
 */
function exactMinor(value: number | string | undefined): bigint | null {
  return exactMinorBig(value)
}

/**
 * An RFC-3339 instant in the format the design draws timestamps in.
 *
 * Returns the raw string unchanged rather than inventing one if the backend
 * sends something unparseable: a wrong booking time on a journal row is worse
 * than an ugly one, because it is what an operator reconciles against the
 * institution's value date.
 *
 * `readAtStamp` is minute-resolution, and it is used anyway rather than adding
 * a second timestamp formatter to the console. Within-minute ordering is read
 * from the posting id, which is the first column and is the ordering key the
 * cursor pages on.
 */
function stamp(iso: string): string {
  if (iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

function provenanceMeta(p: ProvenanceView | undefined): string {
  if (p === undefined) return ''
  const at = stamp(p.read_at)
  return at === '' ? p.source : `${p.source} · read ${at}`
}

// ---------------------------------------------------------------------------
// Rows, after narrowing
// ---------------------------------------------------------------------------

/** A leg amount that can be rendered, or the reason it cannot be. */
type Placement =
  | { readonly ok: true; readonly minor: bigint; readonly currency: string }
  | { readonly ok: false; readonly reason: string }

interface Leg {
  readonly id: number | 'unreadable' | null
  readonly accountId: number | 'unreadable' | null
  readonly accountClass: string
  readonly accountScope: string
  readonly amount: Placement
  readonly meta: string
  readonly authority: string
  /**
   * When the leg was read, raw off the wire.
   *
   * Kept because the single-posting response has NO `generated_at` — the whole
   * payload is one bare `ledgerPostingView` — so the only statement anywhere in
   * it about when the read happened is the provenance stamp the leg composition
   * puts on each amount. Dropping it would leave the detail screen's freshness
   * chip blank, and an undated balance figure is how a stale number gets acted
   * on.
   */
  readonly readAt: string
}

interface Posting {
  readonly id: number | 'unreadable' | null
  /** `external | internal`, raw, so an unknown value can be shown verbatim. */
  readonly kind: string
  readonly postingType: string
  readonly currency: string
  readonly providerRef: string
  readonly idempotencyKey: string
  readonly corrects: number | 'unreadable' | null
  readonly reverses: number | 'unreadable' | null
  readonly occurredAt: string
  readonly postedAt: string
  readonly actor: string
  readonly reasonCode: string
  /**
   * THREE states. `undefined` means the read did not look at the legs, which is
   * always the case on the list and never the case on the detail.
   */
  readonly touchesCustomerValue: boolean | undefined
  /** `undefined` means legs were not read; `[]` means read and there were none. */
  readonly legs: readonly Leg[] | undefined

  /**
   * How many legs arrived and could not be read. Zero is the normal case.
   *
   * Kept separate from `legs` because the two answer different questions: the
   * array says what is on screen, this says whether that is all of it. A screen
   * that only has the first cannot tell a two-leg posting from a three-leg
   * posting with one unreadable leg, and would call both of them balanced.
   */
  readonly legsUnreadable: number
}

/**
 * Whether one leg amount may be rendered, and if not, why not.
 *
 * Lifted from `placeFigure` in `dashboard.ts` and it makes the same four
 * refusals, three of which fire while the backend says `available`: a value it
 * did not send, an amount past exact integer range, and an amount whose
 * currency is not an ISO-4217 code. The last one is not hypothetical politeness
 * — `formatMinor` THROWS on anything that is not three letters, and it is
 * called during render, so a `"EU"` off the wire would take the whole journal
 * to the error boundary rather than produce a named absence.
 *
 * The law-12 refusal is here too. A ledger leg has no business arriving
 * analytics-sourced, and if one ever does the console withholds it rather than
 * badging it: the mirror is eventually consistent and never a source of truth,
 * and there is no freshness stamp that would make a mirror figure safe to sum
 * against a ledger figure.
 */
function placeAmount(f: FigureView | undefined): Placement {
  if (f === undefined) {
    return { ok: false, reason: 'the leg arrived with no amount field at all.' }
  }

  const availability = availabilityOf(f.availability)
  if (availability !== 'available') {
    return { ok: false, reason: reasonFor(availability, f.availability, f.reason) }
  }
  if (f.value === undefined) {
    return {
      ok: false,
      reason: 'the backend reported this leg as available and sent no amount. This is not a leg of zero.',
    }
  }
  if (f.provenance?.authority === 'analytics') {
    return {
      ok: false,
      reason: 'refused — reported as analytics-sourced, and no ledger figure on this console may come from the analytics mirror (law 12).',
    }
  }

  const minor = exactMinor(f.value.amount_minor)
  if (minor === null) {
    return {
      ok: false,
      reason: 'the amount did not arrive as an integer this console can state exactly, so it cannot be stated at all.',
    }
  }
  if (!isCurrencyCode(f.value.currency)) {
    return {
      ok: false,
      reason: f.value.currency === ''
        ? 'the amount arrived with no currency at all, so it cannot be stated (invariant 4).'
        : `the amount arrived with ${JSON.stringify(f.value.currency)}, which is not an ISO-4217 code, so it cannot be stated (invariant 4).`,
    }
  }

  // Normalised, not passed through. `money()` upper-cases before it validates,
  // so `"eur"` is a good code — but the ISO span beside the amount renders this
  // string verbatim, and an operator summing legs should not have to decide
  // whether `eur` and `EUR` are the same currency.
  return { ok: true, minor, currency: f.value.currency.trim().toUpperCase() }
}

function readLeg(raw: unknown): Leg | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const l = raw as Partial<LedgerLegView>
  const amount = l.amount
  return {
    id: idRef(l.id),
    accountId: idRef(l.account_id),
    accountClass: text(l.account_class),
    accountScope: text(l.account_scope),
    amount: placeAmount(amount),
    meta: provenanceMeta(amount?.provenance),
    authority: text(amount?.provenance?.authority),
    readAt: text(amount?.provenance?.read_at),
  }
}

/**
 * One wire row, or `null` if it is not a row at all.
 *
 * `typeof [] === 'object'`, so an array has to be refused explicitly. Without
 * that a `[]` on the wire narrows to a posting with every field absent and is
 * COUNTED AS READ — a ghost row of dashes that inflates the page count and that
 * the unreadable count never mentions.
 *
 * `touches_customer_value` is read as a strict boolean and anything else stays
 * `undefined`. Coercing it would destroy the exact distinction the `*bool`
 * exists for.
 */
function readPosting(raw: unknown): Posting | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const p = raw as Partial<LedgerPostingView>

  // Legs that could not be read are COUNTED, not merely dropped.
  //
  // `.filter(Boolean)` on its own makes an unreadable leg indistinguishable
  // from one the response never carried — and the balance note below is
  // arithmetic over whatever survives, so a silently discarded leg lets the
  // screen state "the legs returned sum to zero in every currency", in green,
  // over a response that carried a leg nobody read. That note's own body
  // promises it is "arithmetic over the rows above and nothing else", which
  // stops being true the moment a returned row is missing from the arithmetic.
  //
  // The list path one level up already counts exactly this failure. This is the
  // same count, kept for the same reason.
  const rawLegs = p.legs
  const parsed = Array.isArray(rawLegs) ? rawLegs.map(readLeg) : undefined
  const legs = parsed?.filter((l): l is Leg => l !== null)
  const legsUnreadable = parsed === undefined ? 0 : parsed.length - (legs?.length ?? 0)

  return {
    legsUnreadable,
    id: idRef(p.id),
    kind: text(p.kind),
    postingType: text(p.posting_type),
    currency: text(p.currency),
    providerRef: text(p.provider_ref),
    idempotencyKey: text(p.idempotency_key),
    corrects: idRef(p.corrects_posting_id),
    reverses: idRef(p.reverses_posting_id),
    occurredAt: text(p.occurred_at),
    postedAt: text(p.posted_at),
    actor: text(p.actor),
    reasonCode: text(p.reason_code),
    touchesCustomerValue: typeof p.touches_customer_value === 'boolean'
      ? p.touches_customer_value
      : undefined,
    legs,
  }
}

/** The page descriptor, after narrowing. Every field can go missing. */
interface Page {
  readonly limit: number | null
  readonly returned: number | null
  /**
   * Read as "anything that is not literally `false`".
   *
   * This is the one flag on the payload whose safe direction is obvious: saying
   * a truncated page is complete is the failure this whole screen guards
   * against, and saying a complete page might be truncated costs an operator
   * one extra glance at a cursor that is not there.
   */
  readonly hasMore: boolean
  readonly nextCursor: number | 'unreadable' | null
}

function readPage(raw: unknown): Page {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { limit: null, returned: null, hasMore: true, nextCursor: null }
  }
  const p = raw as Partial<PageView>
  const limit = idRef(p.limit)
  const returned = typeof p.returned === 'number' && Number.isSafeInteger(p.returned)
    ? p.returned
    : null
  return {
    limit: typeof limit === 'number' ? limit : null,
    returned,
    hasMore: p.has_more !== false,
    nextCursor: idRef(p.next_cursor),
  }
}

// ---------------------------------------------------------------------------
// FR-5.5 — the pairing, and the three states of knowing about it
// ---------------------------------------------------------------------------

/**
 * Whether this posting's provider reference is the evidence FR-5.5 requires.
 *
 * Every branch below exists because collapsing any two of them tells an
 * operator something false:
 *
 *   - moved customer value, reference present → the pairing holds. This is the
 *     only green on the screen, and it is earned rather than assumed.
 *   - moved customer value, NO reference → the shape of unlicensed e-money
 *     issuance by the operator (invariant 2b). Critical, and worded as an
 *     observation rather than a verdict, because a console read is not an
 *     investigation.
 *   - internal, no reference → correct and unremarkable. Neutral.
 *   - internal, reference present → also fine; an internal posting derived from
 *     a provider event legitimately carries one.
 *   - NOT KNOWN, reference present → the reference is there; the pairing is
 *     still unverified because nothing said what the legs touch.
 *   - NOT KNOWN, no reference → the dangerous one. This is every row of the
 *     list read, and rendering it as "internal · no reference expected" would
 *     be the console inventing the fact that makes an FR-5.5 breach invisible.
 *     Attention tone, and the words say to open the posting.
 */
function pairingChip(p: Posting): ChipCell {
  const hasRef = p.providerRef !== ''
  if (p.touchesCustomerValue === true) {
    return hasRef
      ? ['moved customer value · provider ref present', 'g']
      : ['moved customer value · NO provider reference', 'r']
  }
  if (p.touchesCustomerValue === false) {
    return hasRef
      ? ['internal · carries a provider ref', 'n']
      : ['internal · no provider ref expected', 'n']
  }
  return hasRef
    ? ['provider ref present · legs not read', 'n']
    : ['undetermined · legs not read on this list', 'a']
}

/** The FR-5.5 verdict in a sentence, for the detail screen and the notes. */
function pairingSentence(p: Posting): string {
  if (p.touchesCustomerValue === true) {
    return p.providerRef === ''
      ? 'This posting moved customer e-money and carries no provider reference. FR-5.5 requires that every movement of customer value be executed at the institution first and posted from the resulting provider event, so a posting on a value path with no provider reference is not a missing field — it is the shape of value the operator issued itself (invariant 2b).'
      : 'This posting moved customer e-money and carries the provider reference FR-5.5 requires. The pairing holds on this read.'
  }
  if (p.touchesCustomerValue === false) {
    return 'No leg of this posting touches a customer wallet account, so FR-5.5 requires no provider reference. This is an internal movement.'
  }
  return 'This read did not report whether the posting touched customer value — the list does not join the legs. So the presence or absence of a provider reference cannot be judged here, and an absent one is UNDETERMINED rather than expected. Open the posting to resolve it.'
}

// ---------------------------------------------------------------------------
// The journal table
// ---------------------------------------------------------------------------

/**
 * Eight columns, and not the corpus's seven.
 *
 * The corpus draws Debit, Credit and Amount. This read sends none of the three:
 * legs are deliberately absent from the list, because joining them would turn
 * one indexed range scan into a join across the two largest tables in the
 * system. All three are DROPPED rather than filled — an `a` cell is
 * structurally `[minor, currency]`, and `[0, 'EUR']` on a journal row is a
 * fabricated figure on the evidence table. Their absence is named in the
 * screen's own provenance, which is what keeps the badge honest at `Partial`.
 *
 * In their place are the two things this read CAN answer and the corpus does
 * not draw: whether the FR-5.5 pairing can be judged at all, and whether this
 * posting is an answer to an earlier one.
 */
const COLS: readonly Column[] = [
  ['Posting', 'm', '104px'],
  ['Booked (UTC)', 's', 'minmax(168px,1.1fr)'],
  ['posting_type', 's', 'minmax(150px,1.1fr)'],
  ['Currency', 'x', '86px'],
  ['Provider reference', 'm', 'minmax(178px,1.2fr)'],
  ['FR-5.5 pairing', 'c', '250px'],
  ['Answers', 'm', '156px'],
  ['Actor', 's', 'minmax(140px,1fr)'],
]

function postingCell(p: Posting): string {
  if (p.id === 'unreadable') return '— id not exact'
  return p.id === null ? '— no id' : String(p.id)
}

/**
 * Booked, with the institution's own instant beneath it.
 *
 * Both are shown because they answer different questions and they disagree
 * routinely: `occurred_at` is when the movement happened at the institution and
 * is what a statement books on (invariant 12), `posted_at` is when we wrote it
 * down. An operator reconciling a value date against the institution needs the
 * first; one investigating a gap in our own processing needs the second.
 */
function bookedCell(p: Posting): SubCell {
  const posted = stamp(p.postedAt)
  const occurred = stamp(p.occurredAt)
  return [
    // Never blank. A blank cell in a timestamp column reads as "just now" to
    // the same glance that reads a blank amount as zero.
    posted === '' ? '— not returned' : posted,
    occurred === '' ? 'occurred — not returned' : `occurred ${occurred}`,
  ]
}

/**
 * The posting type, with `posting_kind` beneath it.
 *
 * `kind` is a two-value closed set in the schema — `external` or `internal` —
 * and it is a filter an operator reaches for, so it earns a place on the row.
 * An unknown value is shown verbatim rather than mapped to the nearest thing it
 * resembles: this console learning a third kind by guessing is how a new
 * posting class gets read as an old one.
 */
function typeCell(p: Posting): SubCell {
  const type = p.postingType === '' ? '— not returned' : p.postingType
  if (p.kind === '') return [type, 'posting_kind — not returned']
  if (p.kind !== 'external' && p.kind !== 'internal') {
    return [type, `posting_kind ${p.kind} — unknown to this console`]
  }
  return [type, p.kind]
}

/**
 * The provider reference, or the shape of its absence.
 *
 * Three different dashes, because the three absences mean three different
 * things and an operator acts differently on each. `— none (internal)` is a
 * fact; `— NONE` on a posting that moved customer value is a finding; `— none
 * recorded` is neither, because nothing on this read said which.
 */
function providerRefCell(p: Posting): string {
  if (p.providerRef !== '') return p.providerRef
  if (p.touchesCustomerValue === true) return '— NONE, and it moved customer value'
  if (p.touchesCustomerValue === false) return '— none (internal posting)'
  return '— none recorded · undetermined'
}

/**
 * Which earlier posting this one answers.
 *
 * The journal is append-only: a wrong posting is never edited, it is answered
 * by a compensating one. This column is where that answer is legible from the
 * list, so a reader scanning a filtered window can see that a movement has
 * already been addressed rather than concluding it is outstanding.
 */
function answersCell(p: Posting): string {
  const parts: string[] = []
  if (p.corrects === 'unreadable') parts.push('corrects an id that is not exact')
  else if (p.corrects !== null) parts.push(`corrects ${String(p.corrects)}`)
  if (p.reverses === 'unreadable') parts.push('reverses an id that is not exact')
  else if (p.reverses !== null) parts.push(`reverses ${String(p.reverses)}`)
  return parts.length === 0 ? '—' : parts.join(' · ')
}

function actorCell(p: Posting): SubCell {
  return [
    p.actor === '' ? '— unattributed' : p.actor,
    p.reasonCode === '' ? '' : p.reasonCode,
  ]
}

function journalRow(p: Posting): readonly Cell[] {
  return [
    postingCell(p),
    bookedCell(p),
    typeCell(p),
    p.currency === '' ? '— not returned' : p.currency,
    providerRefCell(p),
    pairingChip(p),
    answersCell(p),
    actorCell(p),
  ]
}

/**
 * The count under the table.
 *
 * Four separate facts, and they are not interchangeable: how many rows this
 * page drew, whether the journal continues past it, what cap was applied, and
 * how many rows arrived unreadable. Collapsing them is how a truncated page
 * reads as the whole journal — which on the largest table in the system is the
 * default outcome, not an edge case.
 */
function footerLine(page: Page, drawn: number, unreadable: number): string {
  const cap = page.limit === null
    ? 'the page cap was not stated on this read'
    : `page cap ${String(page.limit)}`
  const head = page.hasMore
    ? `${String(drawn)} posting(s) shown · TRUNCATED — the journal continues past this page (${cap})`
    : `${String(drawn)} posting(s) shown · the result ends here for the filter applied (${cap})`
  return unreadable === 0 ? head : `${head} · ${String(unreadable)} row(s) could not be read`
}

function journalTable(rows: readonly Posting[], page: Page, unreadable: number): TableBlock {
  return {
    t: 'table',
    title: 'ledger_postings',
    // Our own record, read now. `provider` would attribute our journal to the
    // institution, which inverts invariant 2 on the one table where the
    // distinction is argued; `analytics` would be a law-12 lie.
    src: 'ledger',
    // No filter was applied by this read, so no facet bar. `false` suppresses
    // it outright rather than drawing an empty one that implies a filter exists
    // — which on a journal would make a partial view look deliberate.
    facets: false,
    cols: COLS,
    rows: rows.map(journalRow),
    footer: footerLine(page, rows.length, unreadable),
    foot2:
      'No debit, credit or amount column: the list read does not join the legs, and an invented figure on the evidence table is worse than an absent one. Append-only — a wrong posting is answered by a compensating posting, never edited, and this console offers no control that writes one.',
  }
}

// ---------------------------------------------------------------------------
// What the page as a whole says
// ---------------------------------------------------------------------------

/**
 * The truncation notice.
 *
 * A note of its own, beside the footer that already says it, because the
 * footer is read after the table and this has to be read before it. A screen
 * silently showing the first 200 of four million postings is not a smaller
 * version of the journal — it is a different claim, and every conclusion drawn
 * from "there is no such posting" on a truncated page is wrong.
 */
function truncationNote(page: Page): NoteBlock {
  const cursor = page.nextCursor === 'unreadable'
    ? 'The next cursor arrived as a value this console cannot state exactly, so paging on from here cannot be described.'
    : page.nextCursor === null
      ? 'The backend reported more rows and sent no cursor, so this console cannot say where the next page starts.'
      : `The next page starts at before=${String(page.nextCursor)}.`

  return {
    t: 'note',
    tone: 'a',
    kicker: 'Truncated',
    title: 'This is one page of the journal, not the journal',
    body: `The backend read one row past the cap and there was one, so more postings match than are shown here. ${cursor} Nothing below is a statement about postings that are not on this page — in particular, a posting absent from this table has not been shown to be absent from the journal.`,
    ref: page.limit === null ? 'page.has_more = true' : `page.limit = ${String(page.limit)} · page.has_more = true`,
  }
}

/**
 * The one finding the page descriptor can produce, as a note rather than a row.
 *
 * # What stood here, and why it does not
 *
 * A `kv` panel titled "This read" with seven rows: how many postings were
 * drawn, whether more lay beyond the page, the cap the backend applied, the
 * next cursor, how many postings answered an earlier one, that the read carries
 * no amounts, and that FR-5.5 pairing was not reported. Each row carried a
 * paragraph explaining itself.
 *
 * Over a journal holding nothing it rendered as a screenful of commentary above
 * an empty table: an operator who opened the journal explorer was told at
 * length what a page is, and was not told a single posting. The facts in it
 * that an operator can act on are already on the table's own footer — how many
 * rows were drawn, whether the result ends here, the cap applied, and how many
 * rows could not be read.
 *
 * What the footer cannot say is this one: that the page descriptor declared a
 * row count the payload does not contain. That is not commentary, it is a
 * silently shortened journal, and it is a finding. So it survives as the block
 * kind this console uses for findings, and only when it is true.
 */
function descriptorAlarm(rows: number, page: Page, unreadable: number): NoteBlock | null {
  if (page.returned === null || page.returned === rows + unreadable) return null
  // The descriptor and the array are written from the same slice in the
  // handler, so they cannot disagree today. Checked anyway, because "the
  // handler agrees with itself" is a property of the handler being read and not
  // of the one this console is deployed against.
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Page descriptor',
    title: `The descriptor declares ${String(page.returned)} rows and ${String(rows + unreadable)} arrived`,
    body: 'Neither number is stated here as the size of the page.',
  }
}

/**
 * The law this screen is answerable to, stated where the work happens.
 *
 * The design corpus draws a "Post correction" composer under this table and it
 * is not rendered. That is structural rather than unfinished, and saying which
 * matters: an operator who thinks the control is coming waits for it, and one
 * who knows there is none goes and writes the compensating posting through the
 * path that attributes it.
 */
function appendOnlyNote(): NoteBlock {
  return {
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'Append-only',
    title: 'There is no edit, delete or correction control on this screen',
    body: 'A wrong posting is never changed. It is answered by a compensating posting that carries its own actor, reason code and provider reference, and both rows stay in the journal for ever — which is what makes a decision reconstructable six months later from the evidence rather than from memory. The corpus draws a correction composer here; this section ships read-only, with no repair, sweep or bulk-clear path of any kind.',
    ref: 'invariant 2 · invariant 9 · console law 5',
  }
}

/**
 * An empty page, said honestly.
 *
 * The backend refuses the two ways a filter can silently match nothing — an
 * unparseable query is a 400 and a filter naming a missing account is a 404 —
 * so an empty page here really is an empty result. That is worth saying,
 * because "no postings match" is otherwise indistinguishable from a screen that
 * quietly dropped a filter, and during an investigation an operator will read
 * it as a finding about the money.
 */
function emptyNote(page: Page): NoteBlock {
  return {
    t: 'note',
    tone: 'n',
    kicker: 'No rows',
    title: page.hasMore
      ? 'No posting on this page, and the backend says there are more'
      : 'No posting matched this read',
    body: page.hasMore
      ? 'The page is empty and the backend reported more rows beyond it, which is a contradiction this console will not resolve in either direction. Nothing here is a statement about the journal.'
      : 'An empty result, not a dropped filter: the backend refuses an unparseable query rather than answering it unfiltered, and refuses a filter naming an account or customer that does not exist rather than returning nothing for it. So this is an answer about the journal — for the filter applied, and for this window only.',
    ref: 'go-wallet#394 · go-wallet#404',
  }
}

// ---------------------------------------------------------------------------
// Composition — the list
// ---------------------------------------------------------------------------

/**
 * What the operator is NOT being told, for the screen's own provenance chip.
 *
 * The amounts and the pairing flag are named because the designed screen shows
 * both and this read answers for neither, which pins the badge at `Partial` for
 * as long as that is true. `Live` claims everything on the screen was read, and
 * a journal without amounts is not that.
 *
 * Both are DERIVED from the rows rather than asserted about the endpoint. The
 * assertion would keep saying "no amounts" on the day the backend starts
 * sending legs on a list read, which is the same failure as a hardcoded source
 * badge: a sentence that was true when it was written and is nobody's job to
 * revisit.
 *
 * Truncation is deliberately NOT in this list. `missing` names figures that
 * were not answered for; a page cap is not an unanswered figure, it is the
 * shape of the read, and folding it in here would make paging indistinguishable
 * from a failure. It is stated three other ways instead — in the footer, in a
 * note of its own, and in the summary.
 */
function missingFrom(rows: readonly Posting[], unreadable: number): string[] {
  const missing: string[] = []
  if (rows.some((p) => p.legs === undefined)) {
    missing.push('debit, credit and amount per posting (the list read does not join the legs)')
  }
  if (rows.some((p) => p.touchesCustomerValue === undefined)) {
    missing.push('whether each posting moved customer value (touches_customer_value is not reported on a list read)')
  }
  if (unreadable > 0) {
    missing.push(`${String(unreadable)} row(s) that arrived in a shape this console could not read`)
  }
  const noId = rows.filter((p) => p.id === null || p.id === 'unreadable').length
  if (noId > 0) {
    missing.push(`${String(noId)} posting id(s) that could not be stated exactly`)
  }
  return missing
}

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so every path — each availability, the
 * truncated page, a row that is not a row — is table-testable without a client
 * (CLAUDE.md Part III).
 */
export function composeLedgerJournal(r: LedgerPostingsResponse): ScreenData {
  const page = readPage(r.page)
  const read = r.postings.map(readPosting)
  const rows = read.filter((p): p is Posting => p !== null)
  const unreadable = read.length - rows.length

  const blocks: Block[] = []
  if (page.hasMore) blocks.push(truncationNote(page))
  blocks.push(journalTable(rows, page, unreadable))
  const alarm = descriptorAlarm(rows.length, page, unreadable)
  if (alarm !== null) blocks.push(alarm)
  if (rows.length === 0) blocks.push(emptyNote(page))
  blocks.push(appendOnlyNote())

  const missing = missingFrom(rows, unreadable)
  const readAt = stamp(text(r.generated_at))
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt }
    : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt, missing }
  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Composition — one posting, with its legs
// ---------------------------------------------------------------------------

const LEG_COLS: readonly Column[] = [
  ['Leg', 'm', '96px'],
  ['Account', 's', 'minmax(200px,1.4fr)'],
  ['ledger_account_class', 'x', 'minmax(150px,1fr)'],
  ['Amount (signed)', 'a', '160px'],
  ['Read from', 'x', 'minmax(160px,1.1fr)'],
]

/**
 * A leg amount as a cell.
 *
 * The block language types an amount cell's minor units as a JS `number`, so a
 * value the string encoding can carry exactly and a double cannot has nowhere
 * to go in that shape. It is rendered as exact text through the same shared
 * formatter instead — losing the amount styling and keeping the digits, which
 * is the right way round. An amount column may hold a plain string; the
 * renderer draws it as text precisely so a placeholder never becomes a zero.
 */
function legAmountCell(placed: Placement): Cell {
  if (!placed.ok) return '— not stated'
  if (placed.minor >= MIN_SAFE && placed.minor <= MAX_SAFE) {
    return [Number(placed.minor), placed.currency]
  }
  return `${formatMinor(placed.minor, placed.currency)} ${placed.currency}`
}

function legRow(l: Leg): readonly Cell[] {
  const account: SubCell = [
    l.accountClass === '' ? '— class not returned' : l.accountClass,
    l.accountId === 'unreadable'
      ? 'account id not exact'
      : l.accountId === null ? 'account id not returned' : `id ${String(l.accountId)}`,
  ]
  return [
    l.id === 'unreadable' ? '— not exact' : l.id === null ? '—' : String(l.id),
    account,
    l.accountScope === '' ? '— scope not returned' : l.accountScope,
    legAmountCell(l.amount),
    l.meta === '' ? '— unattributed' : l.meta,
  ]
}

/**
 * The sum of the legs THIS READ RETURNED, per currency.
 *
 * Computed here from the legs on screen, and said to be. The schema enforces
 * that a posting's legs sum to zero, and asserting "balanced" on that basis
 * would be this console reporting a database constraint rather than the data in
 * front of it — which is exactly the claim that stays true on screen while a
 * leg is quietly missing from the response.
 *
 * Per currency, never blended (invariant 4). A currency with any leg this
 * console could not state has no sum at all: a partial sum of a balanced
 * posting is a non-zero number that reads as a broken one.
 */
function legSums(legs: readonly Leg[]): { readonly lines: readonly string[]; readonly balanced: boolean } {
  const totals = new Map<string, { minor: bigint; count: number; blocked: boolean }>()
  let unstated = 0

  for (const l of legs) {
    if (!l.amount.ok) {
      unstated += 1
      continue
    }
    const key = l.amount.currency
    const entry = totals.get(key) ?? { minor: 0n, count: 0, blocked: false }
    totals.set(key, { minor: entry.minor + l.amount.minor, count: entry.count + 1, blocked: entry.blocked })
  }
  if (unstated > 0) {
    for (const [key, entry] of totals) totals.set(key, { ...entry, blocked: true })
  }

  const lines: string[] = []
  let balanced = totals.size > 0 && unstated === 0
  for (const [currency, entry] of totals) {
    if (entry.blocked) {
      balanced = false
      lines.push(`${currency}: no sum stated — ${String(unstated)} leg(s) on this posting could not be stated, and a sum missing a leg is a wrong sum rather than a smaller one.`)
      continue
    }
    const total = `${formatMinor(entry.minor, currency)} ${currency}`
    if (entry.minor === 0n) {
      lines.push(`${currency}: the ${String(entry.count)} leg(s) returned sum to ${total}, computed here from those legs.`)
    } else {
      balanced = false
      lines.push(`${currency}: the ${String(entry.count)} leg(s) returned sum to ${total}, which is not zero. Either a leg is missing from this response or the posting does not balance — computed here from the legs returned, not asserted from the schema.`)
    }
  }
  if (totals.size === 0 && unstated > 0) {
    lines.push(`No sum can be stated: none of the ${String(unstated)} leg(s) on this posting arrived with an amount this console can state exactly.`)
  }
  return { lines, balanced }
}

function postingIdentity(p: Posting): KvItem[] {
  const providerNote = pairingSentence(p)
  return [
    ['posting id', postingCell(p), '', 'n', 'The ordering key of the journal, and the cursor this list pages on.', 'm'],
    ['posting_type', p.postingType === '' ? '— not returned' : p.postingType, '', 'n', '', 'm'],
    ['posting_kind', p.kind === '' ? '— not returned' : p.kind, '', 'n',
      p.kind === 'external' || p.kind === 'internal' || p.kind === ''
        ? ''
        : 'A posting kind this console does not know. Shown verbatim rather than mapped to the nearest thing it resembles.',
      'm'],
    ['currency', p.currency === '' ? '— not returned' : p.currency, '', p.currency === '' ? 'r' : 'n',
      p.currency === '' ? 'An amount without a currency is not a value this system may carry (invariant 4).' : '', 'm'],
    ['occurred at (institution)', stamp(p.occurredAt) === '' ? '— not returned' : stamp(p.occurredAt), '', 'n',
      'The instant at the institution. Statements book on this, not on when we wrote the row (invariant 12).', 'm'],
    ['posted at (ours)', stamp(p.postedAt) === '' ? '— not returned' : stamp(p.postedAt), '', 'n', '', 'm'],
    ['actor', p.actor === '' ? '— unattributed' : p.actor, '', p.actor === '' ? 'a' : 'n',
      p.actor === '' ? 'A posting with no actor cannot be attributed, which is the one thing a correction has to be.' : '', ''],
    ['reason code', p.reasonCode === '' ? '— none' : p.reasonCode, '', 'n',
      'Reason codes come from the managed registry. A posting written without one is not necessarily wrong; a correction written without one is unreviewable.', 'm'],
    ['provider reference', p.providerRef === '' ? '— none' : p.providerRef,
      p.touchesCustomerValue === true && p.providerRef === '' ? 'FR-5.5' : '',
      p.touchesCustomerValue === true && p.providerRef === '' ? 'r'
        : p.touchesCustomerValue === undefined && p.providerRef === '' ? 'a' : 'n',
      providerNote, 'm'],
    ['idempotency key', p.idempotencyKey === '' ? '— none' : p.idempotencyKey, '', 'n',
      'Caller-supplied and binding, carried unchanged to the institution (invariant 5). Its absence here is not a claim that the instruction had none.', 'm'],
    ['touches customer value', p.touchesCustomerValue === undefined
      ? '— not reported on this read'
      : p.touchesCustomerValue ? 'yes' : 'no',
      '', p.touchesCustomerValue === undefined ? 'a' : 'n',
      'Read from the account classes of the legs, not decided by this console. Absent is not "no".', ''],
    ['answers an earlier posting', answersCell(p), '', 'n',
      'The journal is append-only. A wrong posting is answered by a compensating one; neither row is ever edited or removed.', 'm'],
  ]
}

/**
 * Folds one posting into the blocks a detail screen renders.
 *
 * Exported and pure, and it has no screen registered against it yet: the
 * journal explorer's own loader renders the LIST. This is the read the row
 * click will resolve to, and it exists now because the list cannot answer
 * FR-5.5 and this can — an operator holding a posting id has somewhere to take
 * it, wired to a route or not.
 */
export function composeLedgerPosting(view: LedgerPostingView): ScreenData {
  const p = readPosting(view)
  if (p === null) {
    return unreadableScreen(
      POSTING_SOURCE,
      'The posting endpoint returned a shape this console does not know.',
      null,
    )
  }

  const legs = p.legs ?? []
  const sums = legSums(legs)
  const withheld = legs
    .map((l, i) => (l.amount.ok ? null : `leg ${l.id === 'unreadable' || l.id === null ? `#${String(i + 1)}` : String(l.id)}: ${l.amount.reason}`))
    .filter((s): s is string => s !== null)

  const blocks: Block[] = [
    {
      t: 'kv',
      title: `Posting ${postingCell(p)}`,
      src: 'ledger',
      note: 'Our operational record of one movement. The institution’s record wins any discrepancy, and it is resolved by a new posting rather than by editing this one.',
      items: postingIdentity(p),
    },
  ]

  if (p.legs === undefined || legs.length === 0) {
    // Absent legs and a posting with no legs are different answers, and NEITHER
    // is a posting. Double-entry means at least two legs; a response carrying
    // none of them has told us nothing about the money, and rendering "0 legs ·
    // balanced" over it would report the absence of evidence as evidence of
    // balance. The two are worded apart because one is a response defect and
    // the other is a data defect, and they are chased by different people.
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Legs',
      title: p.legs === undefined
        ? 'This response carried no legs at all'
        : 'This response reported zero legs',
      body: p.legs === undefined
        ? 'The detail read joins ledger_legs, so a response without the field is not a posting with no legs — no such posting exists. Nothing about the amounts, the accounts or the balance of this posting is known here.'
        : 'The field arrived and was empty. A posting is double-entry and cannot have zero legs, so this is a defect in the read or in the row — not a posting that moved nothing.',
      ref: 'ledger_legs · GET /v1/admin/ledger/postings/{id}',
    })
  } else {
    blocks.push({
      t: 'table',
      title: 'ledger_legs',
      src: 'ledger',
      facets: false,
      cols: LEG_COLS,
      rows: legs.map(legRow),
      footer: p.legsUnreadable === 0
        ? `${String(legs.length)} leg(s) returned · debits positive, credits negative, exactly as stored`
        : `${String(legs.length)} leg(s) shown of ${String(legs.length + p.legsUnreadable)} returned — ${String(p.legsUnreadable)} could not be read · debits positive, credits negative, exactly as stored`,
      foot2: sums.lines.length === 0
        ? 'No leg carried an amount, so no sum is stated. This is not a posting of zero.'
        : sums.lines.join(' · '),
    })
    // A leg that could not be read makes the sum unanswerable, not balanced.
    //
    // Green here says "these legs sum to zero", and an operator on the evidence
    // table reads that as "this posting balances and I have seen all of it".
    // With a leg discarded the arithmetic is over a subset, so it can come to
    // zero while the posting does not — and the note would be asserting, in the
    // affirmative, a property of rows it never saw. Amber and a different
    // sentence: the sum is withheld rather than reported wrong.
    blocks.push(p.legsUnreadable > 0
      ? {
        t: 'note',
        tone: 'a',
        kicker: 'Sum of the legs on this read',
        title: 'The legs cannot be summed, because one of them could not be read',
        body: `${String(p.legsUnreadable)} of ${String(legs.length + p.legsUnreadable)} legs returned by the server could not be read, so any total over the rest would be arithmetic over a subset — it could come to zero while the posting does not. The sum is withheld rather than stated. ${sums.lines.join(' ')}`,
        ref: 'withheld · a partial read cannot answer whether a posting balances',
      }
      : {
        t: 'note',
        tone: sums.balanced ? 'g' : 'r',
        kicker: 'Sum of the legs on this read',
        title: sums.balanced
          ? 'The legs returned sum to zero in every currency'
          : 'The legs returned do not sum to zero',
        body: `${sums.lines.join(' ')} This is arithmetic over the rows above and nothing else — the schema enforces that a posting balances, and reporting that enforcement instead of this sum would keep saying "balanced" while a leg is missing from the response.`,
        ref: 'computed from the legs returned · never asserted from the constraint',
      })
  }

  if (p.touchesCustomerValue === true && p.providerRef === '') {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'FR-5.5',
      title: 'This posting moved customer value and carries no provider reference',
      body: pairingSentence(p),
      ref: 'FR-5.5 · invariant 2b',
    })
  }

  if (withheld.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Not shown',
      title: `${String(withheld.length)} leg amount(s) could not be placed`,
      body: withheld.join(' · '),
      ref: 'A missing amount is not an amount of zero.',
    })
  }

  blocks.push(appendOnlyNote())

  const authorities = new Set(legs.map((l) => l.authority).filter((a) => a !== ''))
  const authority = authorities.size === 0
    ? AUTHORITY
    : [...authorities].sort((a, b) => a.localeCompare(b)).join(' + ')

  const missing: string[] = withheld.map((w) => `${w.split(':')[0] ?? 'a leg'} amount`)
  if (p.legsUnreadable > 0) {
    missing.push(`${String(p.legsUnreadable)} leg(s) of this posting that could not be read`)
  }
  if (p.legs === undefined) missing.push('every leg of this posting')
  else if (legs.length === 0) missing.push('the legs of this posting (the field arrived empty)')
  if (p.touchesCustomerValue === undefined) missing.push('whether this posting moved customer value')

  // From the LEGS, not from a `generated_at` — the single-posting response has
  // no such field, and `posted_at` is when the money moved rather than when we
  // looked. Dating a read with the booking time would put a months-old stamp on
  // a figure read a second ago.
  const readAt = stamp(
    legs.map((l) => l.readAt).filter((a) => a !== '').sort((a, b) => a.localeCompare(b)).at(-1) ?? '',
  )
  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: POSTING_SOURCE, authority, readAt }
    : { kind: 'partial', source: POSTING_SOURCE, authority, readAt, missing }
  return { blocks, provenance }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else. Falling back to the design corpus would put
 * invented postings on the evidence table, which is the one place in this
 * console where a fabricated row could end up quoted in a dispute with the
 * institution.
 *
 * The provenance is `unread`, not `partial`: the chip renders one word, and
 * `Partial` says *some of this is real*. Nothing here is.
 */
function unreadableScreen(source: string, reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null ? 'Nothing on this screen was read.' : `decision_ref ${decisionRef}`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Journal',
      title: 'This screen could not be read',
      body: `${reason} No posting, provider reference or pairing below is known — that is different from the journal being empty, and different again from the filter matching nothing. The screen shows none rather than guessing at any.`,
      ref,
    }],
    provenance: { kind: 'unread', source, reason },
  }
}

function isLedgerPostingsResponse(payload: unknown): payload is LedgerPostingsResponse {
  if (typeof payload !== 'object' || payload === null) return false
  const r = payload as Partial<LedgerPostingsResponse>
  return Array.isArray(r.postings)
}

function isPostingView(payload: unknown): payload is LedgerPostingView {
  return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
}

/**
 * Describes a failed read in the operator's terms, keeping its `decision_ref`.
 *
 * Rethrows an `AbortError` unchanged: a navigation away is a cancellation, not
 * a failure to report, and swallowing it would paint "nothing here is known"
 * over a page the operator has already left.
 */
function failure(source: string, what: string, error: unknown): ScreenData {
  if (error instanceof ApiError) {
    return unreadableScreen(
      source,
      `The ${what} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
      error.decisionRef,
    )
  }
  if (error instanceof DOMException && error.name === 'AbortError') throw error
  return unreadableScreen(source, 'The backend could not be reached.', null)
}

/**
 * Reads the journal.
 *
 * Resolves rather than throwing, per the loader contract: a backend that said
 * "I don't know" is a screen the operator can read, not an exception that
 * collapses the journal to a stack trace. A refusal keeps its `decision_ref`
 * (law 13) — the client surfaces it and a screen must not swallow it.
 */
export const loadLedgerJournal: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(LEDGER_POSTINGS_PATH)
  } catch (error) {
    return failure(SOURCE, 'journal endpoint', error)
  }

  if (!isLedgerPostingsResponse(payload)) {
    return unreadableScreen(SOURCE, 'The journal endpoint returned a shape this console does not know.', null)
  }
  return composeLedgerJournal(payload)
}

/**
 * Reads one posting, with its legs.
 *
 * Not a `ScreenLoader`, because it hangs off a posting id rather than off a
 * screen — the same reason `readDecision` is not one. It is exported now so the
 * FR-5.5 question the list cannot answer has somewhere to be answered, whether
 * or not a route is wired to it yet.
 */
export async function readLedgerPosting(api: ApiClient, id: number): Promise<ScreenData> {
  let payload: unknown
  try {
    payload = await api.get<unknown>(ledgerPostingPath(id))
  } catch (error) {
    return failure(POSTING_SOURCE, 'posting endpoint', error)
  }

  if (!isPostingView(payload)) {
    return unreadableScreen(POSTING_SOURCE, 'The posting endpoint returned a shape this console does not know.', null)
  }
  return composeLedgerPosting(payload)
}
